/**
 * The document metadata framework.
 *
 * 25 document types share one chassis. The source system stores numbering,
 * workflows, print templates and permissions as ROWS KEYED BY DOCUMENT TYPE —
 * so those are runtime configuration, not code. A new document type is a
 * `defineDocument()` call and nothing else.
 */
import type { TabKey, ActionKey } from '../enums/index.js'

export type ControlKind =
  | 'text' | 'textarea' | 'number' | 'money' | 'qty' | 'rate' | 'percent'
  | 'date' | 'select' | 'radio' | 'checkbox' | 'lookup'
  /** Formatted text — bold, italic, underline, lists, links (BILL-015). Stored as sanitised HTML. */
  | 'richtext'
  /** One of a fixed set of colour swatches (`options`), shown with a preview chip (SET1-049). */
  | 'colour'
  /** Several of `options`, stored comma-separated — a role's List Tags (SET1-008). */
  | 'multiselect'

export interface FieldDef {
  key: string
  label: string
  control: ControlKind
  required?: boolean
  hint?: string
  /**
   * Server-side searchable reference to a master entity.
   *
   * `scope` narrows the picker to one owner's records — a supplier's own
   * addresses and contacts, rather than every party's (BILL-008). See
   * `LookupScopeSource` and `lookupOwners`.
   */
  lookup?: {
    entity: string; labelField?: string; scope?: readonly LookupScopeSource[]
    /** Narrowed by earlier answers — a supplier's orders, a project's requests (BILL-002). */
    narrow?: readonly LookupNarrowing[]
    /** Several records, stored as an array of ids — two orders on one invoice (BILL-005). */
    multiple?: boolean
  }
  /**
   * What a new document starts with when nothing else answers the field
   * (INV-024). `default` takes the company's default record — the default tax
   * unit, or the first record of the field's `scope` (the registered office,
   * the supplier's billing address); `currentEmployee` takes the person
   * raising it. `none` turns off a default the chassis would otherwise apply.
   * Applied by the server's create-defaults hook, never by the client alone.
   */
  prefill?: 'default' | 'currentEmployee' | 'none'
  options?: ReadonlyArray<{ value: string; label: string }>
  width?: 'full' | 'half'
  /** Locked once the document leaves DRAFT. */
  lockAfterDraft?: boolean
  /**
   * Shown on the form, never typed there: a snapshot the server takes (the
   * supplier's GSTIN, BILL-007), or an answer the wizard fixed for good (a
   * challan's project and type, BILL-047).
   */
  readOnly?: boolean
  /**
   * Read-only while any of these fields holds a value — a purchase invoice's
   * project and job type, which come from the order it names and would only
   * drift from it if retyped (INV-004), but are still asked for on a
   * subcontract invoice raised without an order.
   */
  readOnlyWhenSet?: readonly string[]
  /**
   * Still editable on an APPROVED document, and saved by the header Update
   * action without a revision or a fresh approval — a customer PO number, a
   * remark, the project manager.
   */
  editableAfterApproval?: boolean
  defaultValue?: string
  /**
   * Read from a spreadsheet and kept on the record, but not asked on the form:
   * an item's Tax % follows the Tax chosen, and a second box for the same
   * number was the same question twice.
   *
   * On a document it is a value the server works out and keeps — a material
   * transfer's first warehouse for the code that reads one, its job type from
   * the project (STK-001, STK-003). It stays on the definition, so the
   * Summary, the print data and the list catalogue still read it; the Form
   * tab does not draw it.
   */
  importOnly?: boolean
  /**
   * Shown only while another field on the same form holds one of these values.
   * A purchase order for stock has no project and every warehouse question a
   * project order does not ask; without this the wizard demands both.
   *
   * `equals` matches a fixed list. `isSet` matches "anything at all", which is
   * what an exchange rate needs: it is asked for whenever a currency has been
   * chosen, whichever one it is.
   */
  visibleWhen?: {
    readonly field: string
    readonly equals?: readonly string[]
    readonly isSet?: boolean
  }
  /** Shown only when the company has this setting on. */
  requiresSetting?: string
  /**
   * Shown as a read-only chip beside the first section's title rather than as
   * a control in the body (PROJ1-004, INV-005, STK-002).
   *
   * The field stays on the definition, so everything that reads the form's
   * fields still sees it — the label resolution behind the chip itself, the
   * create defaults, the Summary, the print header and the list catalogue —
   * and the value stays on the record. It is answered in the create wizard or
   * by the server's default, and changed through an action (Change the tax
   * unit), never retyped in the form. `taxUnitChip()` is the usual one:
   *
   *   fields: [f.look('customerId', …), taxUnitChip(), …]
   */
  chip?: boolean
  /**
   * Masters only: the titled card this field is drawn in on the record page
   * (HOME-014). Fields are grouped by it in the order each title first
   * appears; a field without one joins the first card, which is called
   * "General Info". Documents use `SectionDef` instead.
   *
   *   { ...m.text('hsn_sac_code', 'HSN/SAC Code'), section: 'Commercial Info' }
   */
  section?: string
  /**
   * Masters only: editable while the record is being created, read-only once
   * it is saved — an item's Type and Item Category (HOME-014). Enforced by
   * the record page; imports may still correct it.
   */
  lockAfterSave?: boolean
  /**
   * Masters only: marked and checked as required on the record page, but not
   * refused by the server — a spreadsheet import, or an item created in a
   * hurry from a document line, may still leave it empty (HOME-014: an
   * item's Item Category).
   */
  requiredOnForm?: boolean
  /**
   * A text field answered from the Units master: a select of the company's
   * units that stores the unit's CODE — the same text every item and line
   * already holds in its Unit — not the unit's id (HOME-014). A value that
   * names no unit (an old free-text one) is still shown and kept.
   */
  unitPicker?: boolean
}

/**
 * One thing a document picker is narrowed by (see `lookup-narrowing.ts`).
 *
 * `party` and `project` name the form field holding the id; `kind` and `open`
 * are fixed values. Anything with a `field` lists nothing until it is answered.
 */
export type LookupNarrowParam = 'party' | 'project' | 'kind' | 'open'
export interface LookupNarrowing {
  readonly param: LookupNarrowParam
  readonly field?: string
  readonly value?: string
}

/**
 * Whose records a scoped lookup lists.
 *
 * `OURS` is everything the company itself owns — its offices, stores, project
 * sites and warehouses — so a delivery or billing address can never be a
 * supplier's. The other three name a field on the same form that holds the
 * owner's id: `{ owner: 'PARTY', field: 'supplierId' }` lists only that
 * supplier's addresses, and lists nothing until a supplier is chosen.
 */
export type LookupOwner = 'OURS' | 'PARTY' | 'PROJECT' | 'WAREHOUSE'
  /**
   * The suppliers taking part in the RFQ the field names, that have not yet
   * quoted against it — a supplier quotation's Supplier (PUR-026).
   */
  | 'RFQ'
export interface LookupScopeSource {
  readonly owner: LookupOwner
  /** The field holding the owner's id. Not used by `OURS`. */
  readonly field?: string
}

/**
 * The `owners` parameter a scoped lookup sends, from the values on the form.
 *
 * `undefined` for an unscoped field (search everything, as before); `null` when
 * the field is scoped but nothing it is scoped to has been chosen yet — the
 * picker then says "No entries" instead of offering every party's records.
 */
export const lookupOwners = (
  field: Pick<FieldDef, 'lookup'>,
  values: Readonly<Record<string, unknown>>,
): string | null | undefined => {
  const scope = field.lookup?.scope
  if (!scope || scope.length === 0) return undefined
  const parts: string[] = []
  for (const source of scope) {
    if (source.owner === 'OURS') { parts.push('OURS'); continue }
    const id = source.field ? values[source.field] : undefined
    if (typeof id === 'string' && id !== '') parts.push(`${source.owner}:${id}`)
  }
  return parts.length === 0 ? null : parts.join(',')
}

/** Is this field asked for, given what has been answered and switched on? */
export const isFieldVisible = (
  field: FieldDef,
  values: Readonly<Record<string, unknown>>,
  settings?: Readonly<Record<string, boolean | number | string>>,
): boolean => {
  if (field.requiresSetting && settings && settings[field.requiresSetting] !== true) return false
  const when = field.visibleWhen
  if (!when) return true
  const held = String(values[when.field] ?? '')
  if (when.isSet) return held !== ''
  return (when.equals ?? []).includes(held)
}

/** Is this field shown but not typed into, given what the form holds (`readOnly`, `readOnlyWhenSet`)? */
export const isFieldReadOnly = (field: FieldDef, values: Readonly<Record<string, unknown>>): boolean => {
  if (field.readOnly === true) return true
  return (field.readOnlyWhenSet ?? []).some((key) => {
    const held = values[key]
    return Array.isArray(held) ? held.some((v) => v !== '' && v !== null) : held !== undefined && held !== null && held !== ''
  })
}

export interface SectionDef {
  key: string
  /** Section number, printed identically on screen and on the PDF. */
  n?: string
  title: string
  fields: readonly FieldDef[]
  /**
   * Where the card sits on the Form tab. Sections are drawn before the line
   * grids by default; `afterLines` draws this one straight after the primary
   * grid (`def.lines`) and before the further grids — an RFQ's Additional Info
   * between its Material grid and its Supplier List (PUR-001), a purchase
   * order's Terms and Conditions under its lines.
   *
   * For any other arrangement name the blocks in `DocumentDef.formOrder`.
   */
  placement?: 'beforeLines' | 'afterLines'
}

export type ColumnType = 'text' | 'docno' | 'date' | 'money' | 'qty' | 'rate' | 'status' | 'user' | 'bool'

export interface ColumnDef {
  key: string
  label: string
  type?: ColumnType
  width?: number
  /** Money and quantity columns right-align with tabular figures. */
  align?: 'left' | 'right'
  /**
   * In the column chooser and the export, but off the default view — the
   * reference's "Create View" catalogue is far wider than its default list.
   */
  optional?: boolean
}

export interface LineColumnDef extends ColumnDef {
  editable?: boolean
  /** Rendered under the primary cell text, e.g. an item description. */
  secondary?: boolean
  /** A fixed-choice cell rather than free text. */
  options?: ReadonlyArray<{ value: string; label: string }>
  /**
   * A type-ahead against a master, for cells that hold an id.
   *
   * `fills` copies what the pick carries onto other cells of the row when
   * those are still empty — an adjustment's direction from its master, for
   * instance: `{ direction: 'direction' }` reads the pick's `direction` into
   * the row's `direction`.
   */
  lookup?: {
    entity: string
    fills?: Readonly<Record<string, string>>
    /**
     * Narrows the picker to one owner's records, read from the same ROW — a
     * participant's contact among that row's supplier's contacts (PUR-020).
     * `{ owner: 'PARTY', field: 'supplierId' }` names the row's own cell.
     */
    scope?: readonly LookupScopeSource[]
    /** `fills` replaces what the row held, not only empty cells — a new contact's own phones. */
    refill?: boolean
  }
  /**
   * A picker over the rows of another grid on the same document — an
   * estimation line choosing the BOQ line it costs, a BOQ line choosing the
   * billing term it is billed under.
   *
   * The rows come from `picksFrom`, or from the grid's own `parentSection`
   * when that is not set. The value stored is the chosen row's id, or its
   * `seq:<SECTION>:<n>` key while both are still unsaved.
   */
  parentPicker?: boolean
  picksFrom?: LineSectionKey
  hint?: string
  /**
   * Editable only while another cell of the same row holds one of these
   * values — a lump sum has no quantity, only a Material and Labour line has a
   * material rate. Otherwise the cell reads as it is.
   */
  editableWhen?: RowCondition
  /** The same, the other way round: read-only while the cell holds one of these. */
  lockedWhen?: RowCondition
  /**
   * Which master the lookup searches, by what another cell of the row says —
   * a Labour estimate offers labour items only, a lump sum the expense heads.
   * Falls back to `lookup.entity` (or items) when the cell says nothing listed.
   */
  lookupBy?: { readonly column: string; readonly entities: Readonly<Record<string, string>> }
  /**
   * Must be filled before the document saves (where the cell is shown and
   * open on that row). Marked with an asterisk in the column head and in the
   * row dialog; Save stops on the first row missing it and says which
   * (`missingLineCells`).
   */
  required?: boolean
  /**
   * Shown only while another cell of the same row holds one of these values —
   * the row dialog's fields switching on a Line type or a Cost type. In the
   * grid the cell reads empty on rows where it is hidden; in the dialog the
   * field is not drawn at all, and a hidden cell is never required.
   *
   *   { key: 'rate', …, visibleWhen: { column: 'lineType', equals: ['ITEM'] } }
   */
  visibleWhen?: RowCondition
  /**
   * How a fixed-choice cell is asked in the row dialog. `radio` lays a short
   * list out as radio buttons (Line type: Section / Item); the grid itself
   * still draws a select, where there is no room for a row of buttons.
   */
  control?: 'select' | 'radio'
  /** Drawn in the row dialog only, not as a grid column — a flag the grid has no room for. */
  dialogOnly?: boolean
  /**
   * On a row where this cell is hidden (`visibleWhen`), the grid reads that
   * cell instead — an RFQ's Supplier column showing a new supplier's company
   * name (PUR-004). The dialog still asks each on its own row type.
   */
  elseShow?: string
  /**
   * Not a question on rows like this one: the cell reads "-" in the grid and
   * is left out of the row dialog. Its stored value is untouched — a Lumpsum
   * estimate keeps its quantity of 1, it is just not shown as one (PROJ3-014).
   */
  dashWhen?: RowCondition
  /**
   * The cell's label by what another cell of the row says, in the row dialog
   * — an estimate's rate is "Labour Rate" on a Labour line and "Amount" on a
   * Lumpsum one (PROJ3-013). The grid head keeps `label`.
   */
  labelsBy?: { readonly column: string; readonly labels: Readonly<Record<string, string>> }
  /**
   * How a header answer changes the column — a Single Rate project has one
   * rate, so its supply columns go and its installation rate reads "Rate"
   * (PROJ2-024). `hidden` drops the column unless a row already holds a value
   * in it, so nothing stored is ever put out of sight. See `columnsForHeader`.
   */
  whenHeader?: {
    readonly field: string
    readonly equals: readonly string[]
    readonly hidden?: boolean
    readonly label?: string
  }
  /**
   * A fixed choice whose options are another grid's rows on the same
   * document, stored as the text itself — a BOQ line's summary item, one of
   * the Billing Summary Items (PROJ2-008). Unlike `parentPicker` the value is
   * the words, which is what the import, the prints and the reports carry.
   */
  optionsFrom?: { readonly section: LineSectionKey; readonly column: string }
  /**
   * The line's GST, read as the company's slab name — "GST @ 18%" — where the
   * reference prints it (INV-001, PROJ1-039, PUR-030).
   *
   * The cell still holds `taxPct`, the percentage every total, split, ledger
   * posting, print and e-invoice reads. A rate that matches no slab reads as
   * the bare number. When the cell is open the operator picks a slab from the
   * Taxes master (GST records only); that sets `taxPct` and records the pick
   * as the row's `taxRateId`, which the server then prefers to the item's own
   * rate. Choosing another item clears the pick, so the line follows the new
   * item's Tax. `col.tax()` is the column every grid uses.
   */
  taxSlab?: boolean
  /**
   * A small lot mark beside the figure, which says which receipt the line was
   * taken from: the row cell named here holds the words ("GRN/12 · 01-Jul").
   * The reference's From Reserved and From Stock cells carry it, rather than a
   * Lot column of their own (STK-005). Nothing is drawn while the cell is empty.
   */
  lotNote?: string
  /** Values drawn as a coloured badge rather than as text — "Not estimated" in red. */
  badges?: Readonly<Record<string, 'bad' | 'warn' | 'good'>>
  /** Long text: clamped to this many lines in a read-only grid, with "more". */
  clamp?: number
  /**
   * A cell the grid keeps in step as the operator types, because it is
   * nothing but arithmetic on two others: a receipt's accepted quantity is
   * what was delivered less what was rejected. The server works the same
   * figure out on save and is the authority; this is so the row and its
   * amount read right before then.
   */
  formula?:
    /** `floor` keeps it from going below a figure — an accepted quantity is never negative; a variance may be. */
    | { readonly subtract: readonly [string, string]; readonly floor?: string }
    | { readonly sum: readonly string[] }
    /** quantity × rate, as money: a BOQ line's supply amount and installation amount. */
    | { readonly product: readonly [string, string] }
  /**
   * Choosing one of this cell's options also fills other cells of the row —
   * a Lumpsum cost type makes the quantity 1.
   */
  sets?: Readonly<Record<string, Readonly<Record<string, string>>>>
}

/** A test on another cell of the same row: one of these values, or anything at all. */
export interface RowCondition {
  readonly column: string
  readonly equals?: readonly string[]
  readonly isSet?: boolean
}

export interface FilterDef {
  key: string
  label: string
  control: 'text' | 'select' | 'dateRange' | 'lookup' | 'status'
  required?: boolean
  lookup?: { entity: string }
  options?: ReadonlyArray<{ value: string; label: string }>
}

/**
 * The storage sections a document's lines fall into.
 *
 * One document carries several grids — a project carries its BOQ, the estimate
 * under it and its billing terms; an invoice carries its items, its additional
 * charges and its deductions. They are all `document_line` rows separated by
 * this, which is why a second grid costs a definition rather than a table.
 */
export const LINE_SECTIONS = [
  'ITEM', 'BOQ', 'ESTIMATION', 'CHARGE', 'ADJUSTMENT', 'BILLING_TERM', 'PARTICIPANT',
  'SUMMARY_ITEM',
  // The joint measurement record behind a claimed progress quantity.
  'MEASUREMENT',
  // Free lines beside the BOQ lines on a request, invoice or credit note (BILL-027).
  'INVOICE_ITEM',
] as const
export type LineSectionKey = (typeof LINE_SECTIONS)[number]

/** Where line items may be pulled from, besides typing them by hand. */
export interface LineSourceDef {
  key: string
  label: string
  /** The picker queries this endpoint for candidate lines. */
  source:
    | 'estimation' | 'request' | 'stock' | 'order' | 'boq' | 'excel'
    // docs/24 — the supply-chain pickers.
    | 'requisition' | 'issue' | 'rejection' | 'book' | 'audit' | 'criteria'
    // A transfer's stock held for its project, apart from free stock; and the
    // tools still out with somebody, for their return.
    | 'reserved' | 'outstanding'
    // The invoicing documents': a billing request's open lines, and the
    // additional charges on the orders an invoice names (BILL-011, BILL-038).
    | 'billing' | 'charges'
  /**
   * An Excel source's wording for the sheet download beside it — the
   * reference's "Export Template" on a stock adjustment (STK-012). Defaults
   * to "Download template".
   */
  templateLabel?: string
  /**
   * Offered only while a header field holds one of these values — the
   * estimate and Excel on a stock or tool order, not on a project order,
   * which buys from the site's requests (PUR-029).
   */
  visibleWhen?: FieldDef['visibleWhen']
}

/** One line grid on a document. */
export interface LineGridDef {
  /** Storage section, and the grid's key on the form. Defaults to ITEM. */
  section?: LineSectionKey
  title: string
  n?: string
  columns: readonly LineColumnDef[]
  sources: readonly LineSourceDef[]
  /**
   * Rows here belong to a row in that grid — estimation lines under a BOQ
   * line. The client offers a parent picker; the server stores parent_line_id.
   */
  parentSection?: LineSectionKey
  /** How this grid moves the document total. Defaults to ADD. */
  totalsAs?: 'ADD' | 'SUBTRACT' | 'NONE'
  /** Wording on the button that appends a row. */
  addLabel?: string
  /**
   * How the Summary counts this grid's rows — "Participants: 2 supplier(s)
   * added" rather than "Suppliers invited: 2 lines added" (PUR-022).
   */
  countAs?: { readonly label: string; readonly noun: string }
  /** Shown only when the company has this setting on. */
  requiresSetting?: string
  /** An "Export to Excel" button on the grid that downloads its rows (BILL-026). */
  exportable?: boolean
  /** The export button's wording, where the reference has its own — "Export data" (PUR-023). */
  exportLabel?: string
  /** Shown only while a header field holds one of these values, as on a field. */
  visibleWhen?: FieldDef['visibleWhen']
  /**
   * Two grids over one storage section, told apart by a cell: the estimate
   * under a BOQ line, and the common items and overheads under none. `key`
   * names the grid where the section alone would not.
   */
  key?: string
  rows?: { readonly column: string; readonly isSet: boolean }
  /** Drawn by a purpose-built editor rather than as a plain grid. */
  editor?: 'billingTerms'
  /** A reference given to every new row — the reference's estimation ref (toi8hg). */
  autoRef?: { readonly column: string; readonly style: 'code' | 'number' }
  /**
   * "+ Add" opens the new row in the row dialog rather than appending a blank
   * grid row — the reference's BOQ Detail and Material Info. The dialog shows
   * the grid's columns (and `dialogOnly` ones) that are visible on the row,
   * with their labels, required marks and `control`.
   */
  addOpensDialog?: boolean
  /** The row dialog's title — "BOQ Detail", "Material Info". "Line #n of N" is said beneath it. */
  detailTitle?: string
  /** What the empty grid says — "Add the suppliers participating in this RFQ" (PUR-004). */
  emptyText?: string
  /**
   * Rows come only from the grid's sources while a header field holds one of
   * these values: no "Add" button, and `helper` above the grid in its place —
   * a project purchase order selects from the site's requests (PUR-029).
   */
  pickOnly?: { readonly when: NonNullable<FieldDef['visibleWhen']>; readonly helper: string }
  /**
   * The row dialog asks this cell before anything after it: until the row
   * holds a value there, only the fields up to it are drawn — an estimate's
   * Cost Type, which decides what the rest of the line is (PROJ3-013).
   */
  detailAsksFirst?: string
  /** What a new row starts with — a BOQ line is an Item until said otherwise. */
  rowDefaults?: Readonly<Record<string, string>>
  /**
   * Drawn under each row of the `parentSection` grid rather than as a panel
   * of its own — a BOQ line's estimate, inside "BOQ and Estimation"
   * (PROJ1-008). Each parent row shows its own rows and adds to them.
   */
  nested?: boolean
  /**
   * Draw this grid straight after the form section with this key rather than
   * after all of them — a project's billing terms inside Billing Details
   * (PROJ2-001). Works for `def.lines` as well as `def.grids`.
   */
  afterSection?: string
}

export interface NextDocDef {
  docType: string
  label: string
  /** Only offered once the parent reaches this status. */
  whenStatus?: 'APPROVED' | 'IN_REVIEW'
  /**
   * Only offered while the parent's field holds one of these values — an
   * invoice-type billing request offers a sales invoice, a credit-note-type
   * one a credit note (BILL-031). A blank value matches `''`.
   */
  whenField?: { readonly field: string; readonly equals: readonly string[] }
}

/** The five groups the Transactions menu is organised into (docs/04). */
export type DocumentGroup =
  | 'Project'
  | 'Purchase and Expense'
  | 'Invoice'
  | 'Inventory'
  | 'Tools & Equipment'

export interface DocumentDef {
  /** Stable machine key. Also the `doc_type` discriminator in the database. */
  key: string
  /** Which section of the Transactions menu this appears under. */
  group: DocumentGroup
  /** One or two lines describing it, shown on the menu tile. */
  blurb: string
  /** URL segment: /app/:companyId/page/:route */
  route: string
  label: string
  labelPlural: string
  /** Numbering series key; the series itself is a row, editable per company. */
  numbering: string
  /**
   * Prefix used when a company has no series row yet, so a new document type
   * needs no hand-seeded configuration. `{CO}` is replaced by the company code.
   * Formats follow docs/14. A company may edit the series afterwards.
   */
  numberPrefix: string
  numberWidth?: number
  /**
   * Numbered on its first save rather than at submit — the reference's
   * project reads "PRO/272 • Draft" once saved (PROJ3-001). Such a draft may
   * still be discarded and deleted while it has never been submitted.
   */
  numberOnSave?: boolean
  /** Workflow definition key; resolved and SNAPSHOTTED onto each document. */
  workflow: string
  tabs: readonly TabKey[]
  createWizard: { title: string; subtitle?: string; fields: readonly FieldDef[] }
  form: { sections: readonly SectionDef[] }
  /** The primary grid. Section defaults to ITEM. */
  lines?: LineGridDef
  /** Further grids on the same document: charges, deductions, the estimate. */
  grids?: readonly LineGridDef[]
  /** Default list columns. A saved view overrides these per user. */
  columns: readonly ColumnDef[]
  filters: readonly FilterDef[]
  actions: readonly ActionKey[]
  /** Rule keys evaluated on submit. Definitions live in the rules engine. */
  rules: readonly string[]
  /**
   * A setting this type depends on. When it is off the type stays routable —
   * old links and existing documents still open — but it is off the menu.
   */
  requiresSetting?: string
  /** Set by the server per company, from `requiresSetting`. Never authored. */
  hidden?: boolean
  /** Documents this one can be raised from, and raised into. */
  createsNext?: readonly NextDocDef[]
  /** Does this document allocate against another document's lines? */
  allocatesFrom?: readonly string[]
  /**
   * The Form tab's blocks in an explicit order, when `placement` and
   * `afterSection` are not enough (PROJ2-001: General → Billing and Site →
   * Project Team → Billing Details → Summary Items → BOQ …). Each entry is
   * one of:
   *
   *   '<section key>'           a form section
   *   'grid:<key>'              a grid, by `grid.key ?? grid.section ?? 'ITEM'`
   *                             (the primary grid is `grid:` + its section)
   *   'totalValue'              the Total Value card
   *   'projectFigures'          a project's live figures
   *   'slot:<name>'             a panel the page hands the form — 'slot:projectTeam'
   *
   * Blocks not named keep their default place after the named ones, so an
   * order can never hide a section. See `formBlocks()`.
   */
  formOrder?: readonly string[]
  /**
   * The Summary's header card, where the reference words it for the type
   * (STK-009): whose name is the title, and what the line under it says, in
   * order. `line` names header fields (read as the form shows them) or one of
   * `documentDate` and `integration`; an empty one is left out.
   *
   *   summaryCard: { title: 'project', line: ['transferType', 'documentDate', 'customerId', 'integration'] }
   */
  summaryCard?: { readonly title: 'project' | 'party'; readonly line: readonly string[] }
  /**
   * The Summary's Details, when the reference lists a short set rather than
   * every header field (STK-010): the grid counts first, then these field
   * keys in order — an empty one reads "-" — and `currency` for the
   * document's currency by name.
   *
   * An entry may carry its own label, and `'lines:<grid key>'` places a
   * grid's count where the reference has it (with the noun counted) instead
   * of first — a project's "BOQ: 9 BOQ lines" third (PROJ1-013). A key the
   * presenter works out itself (a project's `team`) is read from it.
   */
  summaryDetails?: ReadonlyArray<string | { readonly key: string; readonly label?: string; readonly noun?: string }>
}

export const defineDocument = <const T extends DocumentDef>(def: T): T => def
